import React from 'react';
import { supabase } from '../../lib/supabaseClient';
import type { ToastAction, ToastTone } from '../../hooks/useToasts';
import * as api from './api';
import type { Task, TaskLead, TaskPatch } from './types';

type PushToast = (message: string, tone?: ToastTone, action?: ToastAction) => string;

/** Live-Refetch bei Änderungen anderer Nutzer (gleiches Muster wie useInvoiceDocumentsLive). */
function useTasksLive(instanceId: string | null, onRefresh: () => void) {
  const onRefreshRef = React.useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  React.useEffect(() => {
    if (!instanceId) return;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => onRefreshRef.current(), 600);
    };
    const filter = `instance_id=eq.${instanceId}`;
    const channel = supabase
      .channel(`tasks:${instanceId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks', filter }, scheduleRefresh)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_checklist_items', filter }, scheduleRefresh)
      .subscribe();
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      void supabase.removeChannel(channel);
    };
  }, [instanceId]);
}

const LINGER_MS = 1500;

export function useTasks({ instanceId, pushToast }: { instanceId: string | null; pushToast: PushToast }) {
  const [tasks, setTasks] = React.useState<Task[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const tasksRef = React.useRef(tasks);
  tasksRef.current = tasks;
  /** Per „Ältere laden" nachgeholte Erledigte — überleben ein Reload der Standardliste. */
  const olderIdsRef = React.useRef(new Set<string>());
  const [hasMoreDone, setHasMoreDone] = React.useState(true);
  const [loadingOlder, setLoadingOlder] = React.useState(false);

  /** Gerade umgeschaltete To-dos bleiben kurz in ihrer Ansicht sichtbar (durchgestrichen). */
  const [lingering, setLingering] = React.useState<ReadonlySet<string>>(() => new Set());
  const lingerTimers = React.useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const linger = React.useCallback((id: string) => {
    const timers = lingerTimers.current;
    const prevTimer = timers.get(id);
    if (prevTimer) clearTimeout(prevTimer);
    setLingering((prev) => new Set(prev).add(id));
    timers.set(
      id,
      setTimeout(() => {
        timers.delete(id);
        setLingering((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, LINGER_MS),
    );
  }, []);
  React.useEffect(() => {
    const timers = lingerTimers.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  const reload = React.useCallback(async () => {
    try {
      const items = await api.fetchTasks();
      const fresh = new Set(items.map((t) => t.id));
      setTasks((prev) => [
        ...items,
        ...prev.filter((t) => olderIdsRef.current.has(t.id) && !fresh.has(t.id)),
      ]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'To-dos konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  useTasksLive(instanceId, () => void reload());

  const replace = React.useCallback((item: Task) => {
    setTasks((prev) => (prev.some((t) => t.id === item.id) ? prev.map((t) => (t.id === item.id ? item : t)) : [item, ...prev]));
  }, []);

  const local = React.useCallback((id: string, fn: (t: Task) => Task) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? fn(t) : t)));
  }, []);

  const fail = React.useCallback(
    (err: unknown) => {
      pushToast(err instanceof Error ? err.message : 'Aktion fehlgeschlagen.', 'error');
      void reload();
    },
    [pushToast, reload],
  );

  const create = React.useCallback(
    async (payload: TaskPatch & { title: string }) => {
      try {
        const item = await api.createTask(payload);
        replace(item);
        pushToast('To-do angelegt');
        return item;
      } catch (err) {
        fail(err);
        return null;
      }
    },
    [fail, pushToast, replace],
  );

  /** Optimistisch übernehmen; `lead` nur bei Änderung der Verknüpfung mitgeben. */
  const update = React.useCallback(
    async (id: string, patch: TaskPatch, lead?: TaskLead | null) => {
      local(id, (t) => ({ ...t, ...patch, ...(lead !== undefined ? { lead } : {}) }));
      try {
        replace(await api.updateTask(id, patch));
      } catch (err) {
        fail(err);
      }
    },
    [fail, local, replace],
  );

  const toggleDone = React.useCallback(
    async (id: string) => {
      const task = tasksRef.current.find((t) => t.id === id);
      if (!task) return;
      const done = !task.done;
      linger(id);
      local(id, (t) => ({ ...t, done, done_at: done ? new Date().toISOString() : null }));
      try {
        replace(await api.updateTask(id, { done }));
        // Nachfolger eines wiederkehrenden To-dos (bzw. dessen Rückzug) sofort zeigen, nicht erst per Realtime.
        if (task.recurrence) void reload();
        if (done) {
          pushToast('To-do erledigt', 'success', {
            label: 'Rückgängig',
            onClick: () => {
              local(id, (t) => ({ ...t, done: false, done_at: null }));
              api
                .updateTask(id, { done: false })
                .then((item) => {
                  replace(item);
                  if (item.recurrence) void reload();
                }, fail);
            },
          });
        }
      } catch (err) {
        fail(err);
      }
    },
    [fail, linger, local, pushToast, reload, replace],
  );

  /** Nächste Seite älterer erledigter To-dos (vor dem ältesten bisher geladenen). */
  const loadOlderDone = React.useCallback(async () => {
    const doneDates = tasksRef.current.filter((t) => t.done && t.done_at).map((t) => t.done_at as string);
    const before = doneDates.length
      ? doneDates.reduce((min, d) => (d < min ? d : min))
      : new Date().toISOString();
    setLoadingOlder(true);
    try {
      const { items, hasMore } = await api.fetchOlderDoneTasks(before);
      items.forEach((t) => olderIdsRef.current.add(t.id));
      setTasks((prev) => {
        const known = new Set(prev.map((t) => t.id));
        return [...prev, ...items.filter((t) => !known.has(t.id))];
      });
      setHasMoreDone(hasMore);
    } catch (err) {
      pushToast(err instanceof Error ? err.message : 'Ältere To-dos konnten nicht geladen werden.', 'error');
    } finally {
      setLoadingOlder(false);
    }
  }, [pushToast]);

  const remove = React.useCallback(
    async (id: string) => {
      const task = tasksRef.current.find((t) => t.id === id);
      if (!task) return;
      setTasks((prev) => prev.filter((t) => t.id !== id));
      try {
        await api.deleteTask(id);
        pushToast('To-do gelöscht', 'success', {
          label: 'Rückgängig',
          onClick: () => {
            replace(task);
            api.restoreTask(id).then(replace, fail);
          },
        });
      } catch (err) {
        fail(err);
      }
    },
    [fail, pushToast, replace],
  );

  const addChecklistItem = React.useCallback(
    async (taskId: string, text: string) => {
      try {
        const item = await api.addChecklistItem(taskId, text);
        local(taskId, (t) => ({ ...t, checklist: [...t.checklist, item] }));
      } catch (err) {
        fail(err);
      }
    },
    [fail, local],
  );

  const toggleChecklistItem = React.useCallback(
    async (taskId: string, itemId: string) => {
      const item = tasksRef.current.find((t) => t.id === taskId)?.checklist.find((c) => c.id === itemId);
      if (!item) return;
      const done = !item.done;
      local(taskId, (t) => ({ ...t, checklist: t.checklist.map((c) => (c.id === itemId ? { ...c, done } : c)) }));
      try {
        await api.updateChecklistItem(taskId, itemId, { done });
      } catch (err) {
        fail(err);
      }
    },
    [fail, local],
  );

  const renameChecklistItem = React.useCallback(
    async (taskId: string, itemId: string, text: string) => {
      local(taskId, (t) => ({ ...t, checklist: t.checklist.map((c) => (c.id === itemId ? { ...c, text } : c)) }));
      try {
        await api.updateChecklistItem(taskId, itemId, { text });
      } catch (err) {
        fail(err);
      }
    },
    [fail, local],
  );

  /** `ids` in neuer Reihenfolge (alle Punkte der Aufgabe). */
  const reorderChecklist = React.useCallback(
    async (taskId: string, ids: string[]) => {
      local(taskId, (t) => {
        const byId = new Map(t.checklist.map((c) => [c.id, c]));
        const checklist = ids.flatMap((id, position) => {
          const item = byId.get(id);
          return item ? [{ ...item, position }] : [];
        });
        return { ...t, checklist };
      });
      try {
        await api.reorderChecklist(taskId, ids);
      } catch (err) {
        fail(err);
      }
    },
    [fail, local],
  );

  const removeChecklistItem = React.useCallback(
    async (taskId: string, itemId: string) => {
      local(taskId, (t) => ({ ...t, checklist: t.checklist.filter((c) => c.id !== itemId) }));
      try {
        await api.deleteChecklistItem(taskId, itemId);
      } catch (err) {
        fail(err);
      }
    },
    [fail, local],
  );

  return {
    tasks,
    loading,
    error,
    lingering,
    hasMoreDone,
    loadingOlder,
    loadOlderDone,
    reload,
    create,
    update,
    toggleDone,
    remove,
    addChecklistItem,
    toggleChecklistItem,
    renameChecklistItem,
    reorderChecklist,
    removeChecklistItem,
  };
}

export type TasksApi = ReturnType<typeof useTasks>;
